import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  updatePassword,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from 'aws-amplify/auth'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'
import {
  User,
  KeyRound,
  CheckCircle,
  Palette,
  ChevronRight,
  Clock,
  ShieldCheck,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { PageHeader } from '@/components'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import type { Role } from '@/types'

const isLocalAuthMode = import.meta.env.VITE_AUTH_MODE === 'local'

const roleLabels: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  employee: 'Employee',
  viewer: 'Viewer',
}

const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type PasswordFormValues = z.infer<typeof passwordFormSchema>

const totpCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
})

type TotpCodeFormValues = z.infer<typeof totpCodeSchema>

type TwoFactorState = 'loading' | 'disabled' | 'enrolling' | 'enabled'

interface TotpEnrollmentDraft {
  setupUri: string
  sharedSecret: string
}

function mapMfaError(err: unknown): string {
  if (err instanceof Error) {
    const message = err.message.toLowerCase()
    if (
      message.includes('software token') ||
      message.includes('enablesoftwaretokenmfa') ||
      message.includes('verifysoftwaretoken') ||
      message.includes('invalid mfa')
    ) {
      return 'That code is incorrect or has expired. Try again with a fresh code from your authenticator app.'
    }
    if (message.includes('invalid verification code') || message.includes('invalid code')) {
      return 'Invalid verification code. Please check and try again.'
    }
    return err.message
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Manage TOTP two-factor authentication enrollment for the signed-in user.
 * Uses Amplify's native Cognito MFA APIs (setUpTOTP / verifyTOTPSetup /
 * updateMFAPreference / fetchMFAPreference). The shared secret is held
 * only in component state for the duration of the enrollment flow — it
 * is never persisted.
 */
function TwoFactorAuthCard({ userEmail }: { userEmail: string | undefined }) {
  const [state, setState] = useState<TwoFactorState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [draft, setDraft] = useState<TotpEnrollmentDraft | null>(null)
  const [enrollError, setEnrollError] = useState<string | null>(null)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false)

  const verifyForm = useForm<TotpCodeFormValues>({
    resolver: zodResolver(totpCodeSchema),
    defaultValues: { code: '' },
  })

  useEffect(() => {
    let cancelled = false
    async function loadPreference() {
      try {
        const preference = await fetchMFAPreference()
        if (cancelled) return
        setState(preference.enabled?.includes('TOTP') ? 'enabled' : 'disabled')
      } catch (err) {
        if (cancelled) return
        setLoadError(mapMfaError(err))
        setState('disabled')
      }
    }
    loadPreference()
    return () => {
      cancelled = true
    }
  }, [])

  const handleBegin = async () => {
    setEnrollError(null)
    setIsEnrolling(true)
    try {
      const details = await setUpTOTP()
      setDraft({
        setupUri: details.getSetupUri('Tap-Tidy', userEmail).toString(),
        sharedSecret: details.sharedSecret,
      })
      setState('enrolling')
      verifyForm.reset({ code: '' })
    } catch (err) {
      setEnrollError(mapMfaError(err))
    } finally {
      setIsEnrolling(false)
    }
  }

  const handleCancelEnrollment = () => {
    setDraft(null)
    setEnrollError(null)
    verifyForm.reset({ code: '' })
    setState('disabled')
  }

  const handleVerify = async (values: TotpCodeFormValues) => {
    setEnrollError(null)
    try {
      await verifyTOTPSetup({ code: values.code })
      await updateMFAPreference({ totp: 'PREFERRED' })
      setDraft(null)
      setState('enabled')
      toast.success(
        "Two-factor authentication is on. You'll be asked for a code on every sign-in."
      )
    } catch (err) {
      setEnrollError(mapMfaError(err))
    }
  }

  const handleDisable = async () => {
    setIsDisabling(true)
    try {
      await updateMFAPreference({ totp: 'DISABLED' })
      setState('disabled')
      setConfirmDisableOpen(false)
      toast.success('Two-factor authentication has been turned off.')
    } catch (err) {
      toast.error(mapMfaError(err))
    } finally {
      setIsDisabling(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Two-factor authentication
        </CardTitle>
        <CardDescription>
          Add an authenticator-app code to every sign-in for an extra layer of
          security.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadError && (
          <Alert variant="destructive">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}

        {state === 'loading' && (
          <p className="text-sm text-muted-foreground">Checking status...</p>
        )}

        {state === 'disabled' && (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary">Off</Badge>
              <span>Two-factor authentication is not enabled on your account.</span>
            </div>
            {enrollError && (
              <Alert variant="destructive">
                <AlertDescription>{enrollError}</AlertDescription>
              </Alert>
            )}
            <Button onClick={handleBegin} disabled={isEnrolling}>
              {isEnrolling ? 'Preparing...' : 'Turn on two-factor authentication'}
            </Button>
          </>
        )}

        {state === 'enrolling' && draft && (
          <>
            <ol className="list-decimal pl-5 text-sm text-muted-foreground space-y-1">
              <li>Open an authenticator app (Authy, 1Password, Google Authenticator).</li>
              <li>Scan the QR code or paste the secret below.</li>
              <li>Enter the 6-digit code the app shows to confirm.</li>
            </ol>

            <div className="flex flex-col items-center gap-3">
              <div className="rounded-lg border bg-white p-4">
                <QRCodeSVG value={draft.setupUri} size={180} level="M" />
              </div>
              <div className="w-full">
                <Label className="text-xs text-muted-foreground">
                  Secret (for manual entry)
                </Label>
                <p className="mt-1 font-mono text-xs break-all bg-muted p-2 rounded">
                  {draft.sharedSecret}
                </p>
              </div>
            </div>

            {enrollError && (
              <Alert variant="destructive">
                <AlertDescription>{enrollError}</AlertDescription>
              </Alert>
            )}

            <Form {...verifyForm}>
              <form
                onSubmit={verifyForm.handleSubmit(handleVerify)}
                className="space-y-3"
              >
                <FormField
                  control={verifyForm.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification code</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="123456"
                          maxLength={6}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={verifyForm.formState.isSubmitting}
                  >
                    {verifyForm.formState.isSubmitting
                      ? 'Verifying...'
                      : 'Verify and turn on'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEnrollment}
                    disabled={verifyForm.formState.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}

        {state === 'enabled' && (
          <>
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-green-600 hover:bg-green-600">On</Badge>
              <span className="text-muted-foreground">
                You'll be asked for a code from your authenticator app every time you sign in.
              </span>
            </div>
            <Button
              variant="outline"
              onClick={() => setConfirmDisableOpen(true)}
              disabled={isDisabling}
            >
              Turn off two-factor authentication
            </Button>
          </>
        )}

        <ConfirmDialog
          open={confirmDisableOpen}
          onOpenChange={setConfirmDisableOpen}
          title="Turn off two-factor authentication?"
          description="Your account will rely on your password alone. You can turn this back on at any time."
          confirmText="Turn off"
          destructive
          isLoading={isDisabling}
          onConfirm={handleDisable}
        />
      </CardContent>
    </Card>
  )
}

export function ProfilePage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  async function onPasswordSubmit(values: PasswordFormValues) {
    setPasswordError(null)
    setPasswordSuccess(false)

    try {
      await updatePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      setPasswordSuccess(true)
      passwordForm.reset()
    } catch (err) {
      const error = err as Error
      if (error.message.includes('Incorrect')) {
        setPasswordError('Current password is incorrect')
      } else if (error.message.includes('policy')) {
        setPasswordError('Password does not meet security requirements')
      } else {
        setPasswordError(error.message || 'Failed to update password')
      }
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile Settings"
        description="View your profile and manage your account"
      />

      {/* Admin Settings Links - hidden for super_admin with no org selected */}
      {(user?.role === 'admin' || (user?.role === 'super_admin' && effectiveOrgId)) && (
        <Card>
          <CardHeader>
            <CardTitle>Organization Settings</CardTitle>
            <CardDescription>
              Manage settings for your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link
              to="/settings/branding"
              className="flex items-center justify-between p-3 -mx-3 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3">
                <Palette className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Branding</div>
                  <div className="text-sm text-muted-foreground">
                    Logo, colors, and visual identity
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
            <Link
              to="/settings/organization"
              className="flex items-center justify-between p-3 -mx-3 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Organization</div>
                  <div className="text-sm text-muted-foreground">
                    Timezone and organization-wide preferences
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Information
            </CardTitle>
            <CardDescription>
              Your account details. Contact an administrator to make changes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Name
              </label>
              <p className="text-lg">{user?.name || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Email
              </label>
              <p className="text-lg">{user?.email || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Role
              </label>
              <div className="mt-1">
                <Badge>{user?.role ? roleLabels[user.role] : 'Unknown'}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {passwordSuccess && (
              <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Password updated successfully!
                </AlertDescription>
              </Alert>
            )}

            {passwordError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            <Form {...passwordForm}>
              <form
                onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter current password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter new password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Confirm new password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  disabled={passwordForm.formState.isSubmitting}
                >
                  {passwordForm.formState.isSubmitting
                    ? 'Updating...'
                    : 'Update Password'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {!isLocalAuthMode && (
          <div className="md:col-span-2">
            <TwoFactorAuthCard userEmail={user?.email} />
          </div>
        )}
      </div>
    </div>
  )
}
